import React, { useCallback } from 'react';
import { useConfigurationContext } from './Context/ConfigurationContext';
import { IMPORT_TYPE_OPTIONS } from './configurationConstants';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import { FeedVersionsTable } from './FeedVersionsTable';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import TextField from 'Components/DesignSystem/TextField/TextField';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Skeleton from 'Components/DesignSystem/Skeleton/Skeleton';
import Status from 'Components/DesignSystem/Status/Status';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { Add, Delete } from 'Components/DesignSystem/Iconography/Icons';
import { getFieldError } from './validationUtils';

type NetworkSetupTabProps = {
    id?: string;
};

/**
 * Configuration → Network Setup. SFTP credentials, feed versions, logging and failure
 * notifications.
 *
 * On DS `TextField` since § 133. The context calls are untouched — `updateNetworkSetup`
 * receives exactly the same partial objects, including `port` as a `Number` and
 * `email_ids` as a whole replacement array — and so is the "keep at least one email
 * row" rule in `handleRemoveEmail`.
 *
 * What the raw inputs cost, and why replacing them is not a restyle:
 *
 * - **`getInputStyle` built the field chrome by string concatenation**, with the
 *   disabled and error variants spelled out inline. Two of its classes
 *   (`tw-min-w-[335px]`, `tw-h-9`) hardcoded what `controlSize.ts` now states once,
 *   and none of the six fields had a focus ring — a keyboard user could not tell which
 *   one they were in (WCAG 2.4.7).
 * - **None of the six inputs was associated with its label.** They rendered a bare
 *   `<label htmlFor="sftp_host">` next to `<input id="sftp_host">`, which does work —
 *   but `Import Type` had `<label htmlFor="import_type">` beside a `Dropdown` that
 *   received `label=""`, so **no element with that id existed at all**: a label
 *   pointing into space, and an unnamed control (WCAG 1.3.1, 3.3.2).
 * - **The error state was a red border and nothing else.** `getFieldError` was read
 *   only to append `tw-border-red-500`; no `aria-invalid`, no message. `TextField`
 *   carries both.
 * - **The "Active" indicator was `<img src={active} alt="Active">`** — a green dot
 *   whose only text was its alt attribute. It is a `Status` chip with a real label.
 * - The loading state was six `SkeletonElement`s from `GMS/Category/Shimmer`; DS
 *   `Skeleton` is the same shape without reaching across modules.
 */
export const NetworkSetupTab = ({ id = 'network-setup-tab' }: NetworkSetupTabProps) => {
    const { currentConfig, isEditMode, isLoading, updateNetworkSetup, validationErrors } = useConfigurationContext();

    const networkSetup = currentConfig?.network_setup;
    const emailIds = networkSetup?.email_ids?.length ? networkSetup.email_ids : [''];
    const hasExistingConnection = networkSetup?.sftp_host !== null && networkSetup?.sftp_host !== '';

    /**
     * Was `handleNetworkChange(e)`, reading `e.target.{name,value,type}`. `TextField`
     * reports the value, so the field name and its coercion come from the call site.
     * `port` is still written as a `Number` — the payload's shape depends on it.
     */
    const handleNetworkChange = (name: string, value: string, asNumber = false) => {
        updateNetworkSetup({ [name]: asNumber ? Number(value) : value });
    };

    const handleImportTypeChange = (value: string | number) => {
        updateNetworkSetup({ import_type: String(value) });
    };

    const handlePartialProcessingChange = useCallback((checked: boolean) => {
        updateNetworkSetup({ partial_processing: checked });
    }, [updateNetworkSetup]);

    const handleEmailChange = useCallback((index: number, value: string) => {
        const updatedEmails = [...(networkSetup?.email_ids || [''])];
        updatedEmails[index] = value;
        updateNetworkSetup({ email_ids: updatedEmails });
    }, [networkSetup?.email_ids, updateNetworkSetup]);

    const handleAddEmail = useCallback(() => {
        const currentEmails = networkSetup?.email_ids || [''];
        updateNetworkSetup({ email_ids: [...currentEmails, ''] });
    }, [networkSetup?.email_ids, updateNetworkSetup]);

    const handleRemoveEmail = useCallback((index: number) => {
        const currentEmails = networkSetup?.email_ids || [''];
        if (currentEmails.length <= 1) return;
        updateNetworkSetup({ email_ids: currentEmails.filter((_, i) => i !== index) });
    }, [networkSetup?.email_ids, updateNetworkSetup]);

    const hasError = (fieldKey: string) => !!getFieldError(validationErrors, fieldKey);

    if (isLoading) {
        return (
            <div className="tw-flex tw-flex-col tw-gap-4" data-testid={`${id}-loading`}>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div className="tw-flex tw-flex-col tw-gap-2" key={index} data-testid={`${id}-skeleton-group-${index}`}>
                        <Skeleton variant="text" width="50%" />
                        <Skeleton variant="rect" height="36px" />
                    </div>
                ))}
            </div>
        );
    }

    if (!networkSetup) return null;

    return (
        <div data-testid={id}>
            <form
                onSubmit={(e) => e.preventDefault()}
                data-testid={`${id}-form`}
                className="tw-flex tw-flex-col tw-gap-4"
            >
                <div data-testid={`${id}-import-type-group`}>
                    <Dropdown
                        label="Import Type"
                        id="import_type"
                        value={networkSetup?.import_type ?? ''}
                        onChange={handleImportTypeChange}
                        placeholder="Select Import Type"
                        options={IMPORT_TYPE_OPTIONS}
                        disabled={!isEditMode}
                        className="tw-max-w-[335px]"
                        usePortal
                        data-testid={`${id}-import-type`}
                    />
                </div>

                <div data-testid={`${id}-sftp-host-group`}>
                    {/* The Active chip sits on the label row, as it always has. */}
                    <div className="tw-flex tw-max-w-[335px] tw-items-center tw-justify-between">
                        <FieldTitle label="SFTP URL" htmlFor="sftp_host" className="tw-mb-2" />
                        {hasExistingConnection && !isEditMode && (
                            <Status
                                tone="success"
                                label="Active"
                                className="tw-mb-2"
                                data-testid={`${id}-active-status`}
                            />
                        )}
                    </div>
                    <TextField
                        label=""
                        id="sftp_host"
                        name="sftp_host"
                        placeholder="Enter url"
                        value={networkSetup.sftp_host || ''}
                        onChange={(value) => handleNetworkChange('sftp_host', value)}
                        disabled={!isEditMode}
                        error={hasError('sftp_host')}
                        className="tw-max-w-[335px]"
                        data-testid={`${id}-sftp-host-input`}
                    />
                </div>

                <div data-testid={`${id}-username-group`}>
                    <TextField
                        label="Username"
                        id="sftp_username"
                        name="sftp_username"
                        placeholder="Enter username"
                        value={networkSetup.sftp_username || ''}
                        onChange={(value) => handleNetworkChange('sftp_username', value)}
                        disabled={!isEditMode}
                        error={hasError('sftp_username')}
                        className="tw-max-w-[335px]"
                        data-testid={`${id}-username-input`}
                    />
                </div>

                <div data-testid={`${id}-password-group`}>
                    <TextField
                        label="Password"
                        type="password"
                        id="sftp_password"
                        name="sftp_password"
                        placeholder="Enter password"
                        value={networkSetup.sftp_password || ''}
                        onChange={(value) => handleNetworkChange('sftp_password', value)}
                        disabled={!isEditMode}
                        error={hasError('sftp_password')}
                        className="tw-max-w-[335px]"
                        data-testid={`${id}-password-input`}
                    />
                </div>

                <div data-testid={`${id}-port-group`}>
                    <TextField
                        label="Port"
                        type="number"
                        id="port"
                        name="port"
                        placeholder="Enter Port"
                        value={networkSetup.port === null || networkSetup.port === undefined ? '' : String(networkSetup.port)}
                        /* `Number`, as before — the saved payload carries a numeric port
                           and the validator checks its range. */
                        onChange={(value) => handleNetworkChange('port', value, true)}
                        disabled={!isEditMode}
                        error={hasError('port')}
                        className="tw-max-w-[335px]"
                        data-testid={`${id}-port-input`}
                    />
                </div>

                <div data-testid={`${id}-file-location-group`}>
                    <TextField
                        label="File location"
                        id="file_location"
                        name="file_location"
                        placeholder="Enter File Location"
                        value={networkSetup.file_location || ''}
                        onChange={(value) => handleNetworkChange('file_location', value)}
                        disabled={!isEditMode}
                        error={hasError('file_location')}
                        className="tw-max-w-[335px]"
                        data-testid={`${id}-file-location-input`}
                    />
                </div>

                <div data-testid={`${id}-feed-versions-group`}>
                    <FieldTitle label="Feed Configurations" className="tw-mb-2" />
                    <FeedVersionsTable id={`${id}-feed-versions`} />
                </div>

                <div id={`${id}-detailed-logging-group`} className="tw-flex tw-items-center tw-gap-3" data-testid={`${id}-detailed-logging-group`}>
                    <Toggle
                        label=""
                        checked={networkSetup.is_debugging}
                        disabled={!isEditMode}
                        onChange={(checked) => updateNetworkSetup({ is_debugging: checked })}
                    />
                    <FieldTitle label="Detailed Logging" />
                </div>

                <div id={`${id}-partial-processing-group`} className="tw-flex tw-items-center tw-gap-3" data-testid={`${id}-partial-processing-group`}>
                    <Toggle
                        label=""
                        checked={networkSetup?.partial_processing ?? false}
                        disabled={!isEditMode}
                        onChange={handlePartialProcessingChange}
                    />
                    <FieldTitle label="Partial Processing on Validation Errors" />
                </div>

                {networkSetup?.partial_processing && (
                    <div id={`${id}-failure-notifications-group`} data-testid={`${id}-failure-notifications-group`}>
                        <div className="tw-flex tw-flex-col tw-gap-3">
                            {emailIds.map((email, index) => {
                                const isLastRow = index === emailIds.length - 1;
                                const canDelete = emailIds.length > 1;

                                return (
                                    <div
                                        key={index}
                                        id={`${id}-email-row-${index}`}
                                        className="tw-flex tw-items-end tw-gap-2"
                                        data-testid={`${id}-email-row-${index}`}
                                    >
                                        <TextField
                                            label={index === 0 ? "Failure notifications" : ""}
                                            placeholder="Enter email address"
                                            value={email}
                                            onChange={(value) => handleEmailChange(index, value)}
                                            disabled={!isEditMode}
                                            error={hasError('email_ids')}
                                            className="tw-w-[335px]"
                                            required={index === 0}
                                            data-testid={`${id}-email-input-${index}`}
                                        />
                                        {/*
                                          Two fixed slots, so the trash sits in the same column on
                                          every row. The Add was a padded `Button size="sm"` (24px
                                          of inset + a 20px glyph = 44px) standing in for a 32px
                                          spacer, which pushed the last row's trash 12px right.
                                          Both are now DS `IconButton` squares (28px) and each
                                          spacer is that same square with the same margin — so
                                          a slot is one width whether it holds a button or not.
                                        */}
                                        {isEditMode && isLastRow ? (
                                            <IconButton
                                                size="sm"
                                                id={`${id}-add-email-btn`}
                                                aria-label="Add another notification email"
                                                onClick={handleAddEmail}
                                                className="tw-mb-1"
                                                data-testid={`${id}-add-email-btn`}
                                            >
                                                <Add />
                                            </IconButton>
                                        ) : (
                                            <div className="tw-w-7 tw-h-7 tw-shrink-0 tw-mb-1" aria-hidden="true" />
                                        )}
                                        {isEditMode && canDelete ? (
                                            <IconButton
                                                variant="danger-subtle"
                                                size="sm"
                                                id={`${id}-delete-email-btn-${index}`}
                                                aria-label={`Remove notification email ${index + 1}`}
                                                onClick={() => handleRemoveEmail(index)}
                                                className="tw-mb-1 tw-ml-10"
                                                data-testid={`${id}-delete-email-btn-${index}`}
                                            >
                                                <Delete />
                                            </IconButton>
                                        ) : (
                                            <div className="tw-w-7 tw-h-7 tw-shrink-0 tw-mb-1 tw-ml-10" aria-hidden="true" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};
